<template>
  <ClientOverviewCard
    :title="teamName ? $t(':client team', { client: teamName }) : $t('Client team')"
    :loading="loading"
  >
    <WdsStack direction-column gap-md data-test="client-contacts-card">
      <WdsStack
        v-if="members.length > 0"
        direction-column
        gap-md
        data-test="client-contacts-list"
      >
        <ClientContactLabel
          v-for="(member, index) in members"
          :key="member.id"
          sm
          :contact="member"
          :note="member.id === ownerId ? $t('Owner') : undefined"
          :data-test="`contact-${index}`"
        />
      </WdsStack>

      <WdsText v-if="members.length === 0" secondary data-test="client-contacts-empty">
        <template v-if="isAgencyLink">{{ $t('No one from the MSP is assigned to this client yet') }}</template>
        <template v-else>{{ $t('No team members known for this client yet') }}</template>
      </WdsText>
      <div v-else class="tw-flex">
        <WdsButtonGhost
          secondary
          class="-tw-mx-1"
          data-test="client-contacts-view-people"
          @click="emit('viewPeople')"
        >
          {{ $tc('View :count person | View all :count people', total) }}
        </WdsButtonGhost>
      </div>
    </WdsStack>
  </ClientOverviewCard>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { WdsButtonGhost, WdsStack, WdsText } from '@worksome/wds-components'
import { FragmentType, graphql, useFragment } from '@/gql'
import { $t, $tc } from '@/app-bootstrapping/SetUpInternationalization'
import ClientContactLabel from '@/components/Clients/ClientContactLabel.vue'
import ClientOverviewCard from '@/components/Clients/OverviewCards/ClientOverviewCard.vue'

const ClientContactsCardFields = graphql(`
  fragment ClientContactsCard on ClientRelation {
    id
    ... on CompanySupplier {
      company {
        id
        name
        owner {
          id
        }
        teamMembersAndOwners(first: 5) {
          data {
            id
            ...ClientContactLabel
          }
          paginatorInfo {
            total
          }
        }
      }
    }
    ... on SupplierClient {
      managingCompany {
        id
        name
      }
      managingCompanyContacts {
        id
        ...ClientContactLabel
      }
    }
  }
`)

const props = defineProps<{
  client: FragmentType<typeof ClientContactsCardFields> | null
  loading: boolean
}>()

const emit = defineEmits<{ (e: 'viewPeople'): void }>()

const relation = computed(() => useFragment(ClientContactsCardFields, props.client ?? null) ?? null)

// On a staffing agency's managed link the MSP's people stand where the client's team would.
const isAgencyLink = computed(() => !!relation.value && 'managingCompanyContacts' in relation.value)

const teamName = computed(() => {
  const { value } = relation
  if (!value) return null

  return 'managingCompany' in value ? value.managingCompany.name : (value.company?.name ?? null)
})

const ownerId = computed(() =>
  relation.value && 'company' in relation.value ? (relation.value.company?.owner?.id ?? null) : null,
)

const members = computed(() => {
  const { value } = relation
  if (!value) return []

  return 'managingCompanyContacts' in value
    ? value.managingCompanyContacts.slice(0, 5)
    : (value.company?.teamMembersAndOwners.data ?? [])
})

const total = computed(() => {
  const { value } = relation
  if (!value) return 0

  return 'managingCompanyContacts' in value
    ? value.managingCompanyContacts.length
    : (value.company?.teamMembersAndOwners.paginatorInfo.total ?? 0)
})
</script>
